// In src/components/GivingInfoEditModal.js

import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, CircularProgress, IconButton, Alert, List, ListItem, ListItemText, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { db } from '../services/firebase';
import { collection, doc, setDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { uploadFile, deleteFileByUrl } from '../services/storageService';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

function GivingInfoEditModal({ open, onClose, givingMethods, churchId }) {
  const { userProfile } = useAuth();
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [methodTitle, setMethodTitle] = useState('');
  const [methodDescription, setMethodDescription] = useState('');
  const [methodLink, setMethodLink] = useState('');
  const [methodAccountHandle, setMethodAccountHandle] = useState('');
  const [methodOrder, setMethodOrder] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (open && selectedMethod) {
      setMethodTitle(selectedMethod.title || '');
      setMethodDescription(selectedMethod.description || '');
      setMethodLink(selectedMethod.link || '');
      setMethodAccountHandle(selectedMethod.accountHandle || '');
      setMethodOrder(selectedMethod.order || '');
      setIconFile(null);
      setQrCodeFile(null);
    } else {
      setMethodTitle('');
      setMethodDescription('');
      setMethodLink('');
      setMethodAccountHandle('');
      setMethodOrder('');
      setIconFile(null);
      setQrCodeFile(null);
    }
    setErrorMessage('');
  }, [open, selectedMethod]);

  const handleCreateNew = () => {
    setSelectedMethod(null);
    setMethodTitle('');
    setMethodDescription('');
    setMethodLink('');
    setMethodAccountHandle('');
    setMethodOrder('');
    setIconFile(null);
    setQrCodeFile(null);
    setErrorMessage('');
  };

  const handleEditExisting = (method) => {
    setSelectedMethod(method);
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (fileType === 'icon') {
        setIconFile(file);
      } else {
        setQrCodeFile(file);
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage('');

    if (!methodTitle.trim()) {
      setErrorMessage("Title is required.");
      setSaving(false);
      return;
    }

    let iconUrl = selectedMethod?.iconUrl || '';
    let qrCodeUrl = selectedMethod?.qrCodeUrl || '';

    try {
      // Handle icon upload
      if (iconFile) {
        if (selectedMethod?.iconUrl) {
          await deleteFileByUrl(selectedMethod.iconUrl);
        }
        const iconPath = `churches/${userProfile.churchId}/givingInfo/${Date.now()}_icon_${iconFile.name}`;
        iconUrl = await uploadFile(iconFile, iconPath);
      }

      // Handle QR code upload
      if (qrCodeFile) {
        if (selectedMethod?.qrCodeUrl) {
          await deleteFileByUrl(selectedMethod.qrCodeUrl);
        }
        const qrCodePath = `churches/${userProfile.churchId}/givingInfo/${Date.now()}_qr_${qrCodeFile.name}`;
        qrCodeUrl = await uploadFile(qrCodeFile, qrCodePath);
      }

      const dataToSave = {
        title: methodTitle,
        description: methodDescription,
        link: methodLink,
        accountHandle: methodAccountHandle,
        icon: iconUrl,
        qrCodeUrl: qrCodeUrl,
        order: parseInt(methodOrder) || 0,
        updatedAt: new Date(),
      };

      if (selectedMethod?.id) {
        const docRef = doc(db, 'churches', userProfile.churchId, 'givingInfo', selectedMethod.id);
        await setDoc(docRef, dataToSave, { merge: true });
      } else {
        await addDoc(collection(db, 'churches', userProfile.churchId, 'givingInfo'), dataToSave);
      }
      setSaving(false);
      onClose();
    } catch (e) {
      console.error("Error saving giving method:", e);
      setErrorMessage(e.message);
      setSaving(false);
    }
  };

  const handleDelete = async (methodId, iconUrl, qrCodeUrl) => {
    if (window.confirm("Are you sure you want to delete this giving method?")) {
      setDeletingId(methodId);
      try {
        if (iconUrl) {
          await deleteFileByUrl(iconUrl);
        }
        if (qrCodeUrl) {
          await deleteFileByUrl(qrCodeUrl);
        }
        await deleteDoc(doc(db, 'churches', userProfile.churchId, 'givingInfo', methodId));
      } catch (e) {
        console.error("Error deleting giving method:", e);
        setErrorMessage(e.message);
      } finally {
        setDeletingId(null);
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" component="h2">Manage Ways to Give</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create and edit the different giving methods available to your members.
        </Typography>

        {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

        <Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNew} sx={{ mb: 2 }}>
            Add New Method
          </Button>
          <List dense>
            {givingMethods.map((method) => (
              <ListItem key={method.id} secondaryAction={
                <>
                  <IconButton edge="end" aria-label="edit" onClick={() => handleEditExisting(method)}>
                    <AddIcon />
                  </IconButton>
                  <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(method.id, method.icon, method.qrCodeUrl)}>
                    {deletingId === method.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                  </IconButton>
                </>
              }>
                <ListItemText primary={method.title} />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* LINT-FIX: Simplified this condition to remove the pointless self-comparison */}
        {selectedMethod !== undefined && (
          <Box sx={{ mt: 3, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>{selectedMethod ? 'Edit Method' : 'Add New Method'}</Typography>
            <TextField fullWidth label="Title (e.g., Venmo, Cash App)" margin="normal" value={methodTitle} onChange={(e) => setMethodTitle(e.target.value)} />
            <TextField fullWidth label="Description" multiline rows={2} margin="normal" value={methodDescription} onChange={(e) => setMethodDescription(e.target.value)} />
            <TextField fullWidth label="Account Handle (e.g., @yourchurch)" margin="normal" value={methodAccountHandle} onChange={(e) => setMethodAccountHandle(e.target.value)} />
            <TextField fullWidth label="Link URL" margin="normal" value={methodLink} onChange={(e) => setMethodLink(e.target.value)} />
            <TextField fullWidth label="Display Order (lower numbers show first)" type="number" margin="normal" value={methodOrder} onChange={(e) => setMethodOrder(e.target.value)} />
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Button variant="outlined" component="label" fullWidth>
                  Upload Icon
                  <input type="file" hidden onChange={(e) => handleFileChange(e, 'icon')} />
                </Button>
                {iconFile && <Typography variant="caption">{iconFile.name}</Typography>}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button variant="outlined" component="label" fullWidth>
                  Upload QR Code
                  <input type="file" hidden onChange={(e) => handleFileChange(e, 'qrCode')} />
                </Button>
                {qrCodeFile && <Typography variant="caption">{qrCodeFile.name}</Typography>}
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setSelectedMethod(null); onClose(); }} sx={{ mr: 1 }}>Cancel</Button>
              <Button onClick={handleSave} variant="contained" disabled={saving}>
                {saving ? <CircularProgress size={24} /> : 'Save Method'}
              </Button>
            </Box>
          </Box>
        )}

        <Button onClick={onClose} sx={{ mt: 2 }}>Close</Button>
      </Box>
    </Modal>
  );
}

export default GivingInfoEditModal;